import React, { useState, useEffect } from 'react';
import { X, Plus, Loader2 } from 'lucide-react';
import { api } from '../utils/apiClient';
import apiClient from '../utils/apiClient';
import { addLog } from '../utils/logger';
import {
  OPCIONES_TIPO_SERVICIO,
  OPCIONES_RESPONSABILIDAD,
  OPCIONES_CHOQUE_SIMPLE,
  OPCIONES_CIERRE_MASTER,
} from '../constants/masterData';

const inputClass =
  'w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-colors duration-200';
const labelClass = 'block text-xs font-medium text-slate-400 mb-1';

const EMPTY = {
  aseguradora: '',
  expediente: '',
  fecha: '',
  nombre: '',
  telefono: '',
  placa: '',
  direccion: '',
  cliente_corporativo: '',
  cuenta: '',
  hora_asignacion: '',
  hora_llegada: '',
  tipo_servicio: '',
  responsabilidad: '',
  cierre_servicio: '',
  cierre_choque_simple: '',
  abogado: '',
  asunto: '',
  conciliacion: '',
  observacion: '',
  kilometraje: '',
};

const NuevoCasoModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [aseguradorasFull, setAseguradorasFull] = useState([]); // incluye id para opciones
  const [dynamicOpciones, setDynamicOpciones] = useState(null);
  const [loadingOpciones, setLoadingOpciones] = useState(false);
  // Issue 4 (diner 2026-04-27): abogado debe ser dropdown, no input texto libre.
  const [abogadosList, setAbogadosList] = useState([]);

  useEffect(() => {
    apiClient.get('/clientes-config')
      .then(res => {
        setAseguradoras(res.data.map(c => ({ value: c.slug, label: c.nombre_display })));
        setAseguradorasFull(res.data);
      })
      .catch(() => { setAseguradoras([]); setAseguradorasFull([]); });

    // Issue 4: cargar lista de abogados (solo Abogado / Abogado_Seguimiento / Abogado_demandas)
    api.get('/usuarios')
      .then(users => {
        const abogados = (users || [])
          .filter(u => /abogado/i.test(u.rol || ''))
          .filter(u => u.estado === 1 || u.estado === undefined)
          .map(u => ({ num_doc: String(u.num_doc || '').trim(), nombre: (u.nombre || '').trim() }))
          .filter(u => u.num_doc && u.nombre)
          .sort((a, b) => a.nombre.localeCompare(b.nombre));
        setAbogadosList(abogados);
      })
      .catch(() => setAbogadosList([]));
  }, []);

  // Cargar opciones dinámicas SOLO de la aseguradora seleccionada
  // (anti-cruce IKE/Asissprex). NUNCA mostrar opciones de otra aseguradora.
  useEffect(() => {
    if (!form.aseguradora) { setDynamicOpciones(null); return; }
    const cliente = aseguradorasFull.find(c => c.slug === form.aseguradora);
    if (!cliente?.id) { setDynamicOpciones(null); return; }
    let mounted = true;
    setLoadingOpciones(true);
    api.get(`/clientes-config/${cliente.id}/opciones`)
      .then(opciones => { if (mounted) setDynamicOpciones(opciones); })
      .catch(() => { if (mounted) setDynamicOpciones(null); })
      .finally(() => { if (mounted) setLoadingOpciones(false); });
    return () => { mounted = false; };
  }, [form.aseguradora, aseguradorasFull]);

  // SOLO opciones del catálogo dinámico — sin fallback a masterData binario.
  const tiposServicio = dynamicOpciones?.tipo_servicio || [];
  const responsabilidades = dynamicOpciones?.responsabilidad || [];
  const choquesSimple = dynamicOpciones?.cierre_choque_simple || [];
  const cierresServicio = dynamicOpciones?.cierre_servicio || [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.aseguradora) { setError('Selecciona la aseguradora.'); return; }
    if (!form.expediente.trim()) { setError('El expediente es obligatorio.'); return; }

    // Warning tipo<->cierre incoherente (no bloquea, solo pregunta)
    const idCierreNum = parseInt(form.cierre_servicio, 10);
    const idTipoNum = parseInt(form.tipo_servicio, 10);
    if (!isNaN(idCierreNum) && idCierreNum !== 999 && !isNaN(idTipoNum)) {
      const cierreInfo = (cierresServicio || []).find(x => x.id === idCierreNum);
      const tipoCierre = cierreInfo?.id_tipo_servicio;
      if (tipoCierre && tipoCierre !== idTipoNum) {
        const labelCierre = cierreInfo?.label || idCierreNum;
        const labelTipoCierre = ((tiposServicio || []).find(x => x.id === tipoCierre) || {}).label || tipoCierre;
        const labelTipoCaso = ((tiposServicio || []).find(x => x.id === idTipoNum) || {}).label || idTipoNum;
        const ok = window.confirm(
          `El cierre "${labelCierre}" (${idCierreNum}) corresponde al tipo de servicio ` +
          `"${labelTipoCierre}" (${tipoCierre}), no a "${labelTipoCaso}" (${idTipoNum}). ` +
          `\n\n¿Quieres guardarlo así de todos modos?`
        );
        if (!ok) return;
      }
    }

    setSaving(true);
    try {
      const payload = { ...form };
      // Campos que no admiten cadena vacía en BD (Date / BigInt / Float)
      ['fecha', 'telefono', 'kilometraje'].forEach(k => {
        if (payload[k] === '' || payload[k] === null || payload[k] === undefined) delete payload[k];
      });
      // Selects: enviar null si vacío (en lugar de omitir) para permitir limpiar
      ['tipo_servicio', 'responsabilidad', 'cierre_servicio', 'cierre_choque_simple'].forEach(k => {
        if (payload[k] === '' || payload[k] === null || payload[k] === undefined) {
          payload[k] = null;
        } else {
          const n = parseInt(payload[k], 10);
          if (!isNaN(n)) payload[k] = n;
        }
      });
      // Trim a campos de texto para evitar espacios al inicio/fin
      ['expediente', 'cliente_corporativo', 'nombre', 'placa', 'direccion', 'cuenta', 'abogado', 'asunto', 'conciliacion', 'observacion'].forEach(k => {
        if (typeof payload[k] === 'string') payload[k] = payload[k].trim();
      });

      await api.post('/casos', payload);
      addLog(`Caso creado — expediente ${form.expediente}`, 'success', 'Casos');
      onCreated();
      onClose();
    } catch (err) {
      const detalle = err?.backendMessage && err.backendMessage !== 'Ocurrió un error. Por favor, intenta nuevamente'
        ? err.backendMessage
        : (err?.message || 'Error al crear el caso.');
      setError(detalle);
      addLog(`Error al crear caso — expediente ${form.expediente}: ${detalle}`, 'error', 'Casos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-gradient-to-r from-slate-800/50 to-slate-900 rounded-t-3xl flex items-center justify-between flex-none">
          <div>
            <h2 className="text-lg font-semibold text-white">Nuevo Caso</h2>
            <p className="text-xs text-slate-400 mt-0.5">Completa los datos para registrar el caso</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="overflow-y-auto custom-scrollbar flex-1">
          <div className="p-6 space-y-5">

            {error && (
              <div className="px-4 py-3 rounded-xl bg-red-900/30 border border-red-800/50 text-red-300 text-sm">
                {error}
              </div>
            )}

            {/* Aseguradora + Expediente */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Aseguradora <span className="text-red-400">*</span></label>
                <select name="aseguradora" value={form.aseguradora} onChange={handleChange} className={inputClass} required>
                  <option value="">Seleccionar...</option>
                  {aseguradoras.map(a => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Expediente <span className="text-red-400">*</span></label>
                <input name="expediente" value={form.expediente} onChange={handleChange} className={inputClass} placeholder="Ej: IKE-2024-001" required />
              </div>
            </div>

            {/* Fecha + Placa */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Fecha</label>
                <input type="date" name="fecha" value={form.fecha} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Placa</label>
                <input name="placa" value={form.placa} onChange={handleChange} className={inputClass} placeholder="ABC-123" />
              </div>
            </div>

            {/* Nombre + Teléfono */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Nombre cliente</label>
                <input name="nombre" value={form.nombre} onChange={handleChange} className={inputClass} placeholder="Nombre completo" />
              </div>
              <div>
                <label className={labelClass}>Teléfono</label>
                <input type="number" name="telefono" value={form.telefono} onChange={handleChange} className={inputClass} placeholder="3001234567" />
              </div>
            </div>

            {/* Dirección */}
            <div>
              <label className={labelClass}>Dirección</label>
              <input name="direccion" value={form.direccion} onChange={handleChange} className={inputClass} placeholder="Dirección del siniestro" />
            </div>

            {/* Cliente corporativo + Cuenta */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Cliente corporativo</label>
                <input name="cliente_corporativo" value={form.cliente_corporativo} onChange={handleChange} className={inputClass} placeholder="Ej: SURA" />
              </div>
              <div>
                <label className={labelClass}>Cuenta</label>
                <input name="cuenta" value={form.cuenta} onChange={handleChange} className={inputClass} placeholder="Número de cuenta" />
              </div>
            </div>

            {/* Hora asignación + Hora llegada */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Hora asignación</label>
                <input name="hora_asignacion" value={form.hora_asignacion} onChange={handleChange} className={inputClass} placeholder="HH:MM" />
              </div>
              <div>
                <label className={labelClass}>Hora llegada</label>
                <input name="hora_llegada" value={form.hora_llegada} onChange={handleChange} className={inputClass} placeholder="HH:MM" />
              </div>
            </div>

            {/* Tipo servicio + Responsabilidad */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Tipo de servicio</label>
                <select name="tipo_servicio" value={form.tipo_servicio} onChange={handleChange} className={inputClass} disabled={!form.aseguradora}>
                  <option value="">Seleccionar...</option>
                  {tiposServicio.map(o => (
                    <option key={o.id} value={String(o.id)}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Responsabilidad</label>
                <select name="responsabilidad" value={form.responsabilidad} onChange={handleChange} className={inputClass} disabled={!form.aseguradora}>
                  <option value="">Seleccionar...</option>
                  {responsabilidades.map(o => (
                    <option key={o.id} value={String(o.id)}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Cierre servicio + Choque simple */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Cierre de servicio</label>
                <select name="cierre_servicio" value={form.cierre_servicio} onChange={handleChange} className={inputClass} disabled={!form.aseguradora}>
                  <option value="">{form.aseguradora ? 'Seleccionar...' : 'Selecciona aseguradora primero'}</option>
                  {cierresServicio.map(o => (
                    <option key={o.id} value={String(o.id)}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Choque simple</label>
                <select name="cierre_choque_simple" value={form.cierre_choque_simple} onChange={handleChange} className={inputClass} disabled={!form.aseguradora}>
                  <option value="">Seleccionar...</option>
                  {choquesSimple.map(o => (
                    <option key={o.id} value={String(o.id)}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Abogado + Conciliación */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Abogado</label>
                <select name="abogado" value={form.abogado} onChange={handleChange} className={inputClass}>
                  <option value="">Seleccionar abogado...</option>
                  {abogadosList.map(a => (
                    <option key={a.num_doc} value={a.num_doc}>{a.nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Conciliación</label>
                <input name="conciliacion" value={form.conciliacion} onChange={handleChange} className={inputClass} placeholder="Conciliación" />
              </div>
            </div>

            {/* Asunto */}
            <div>
              <label className={labelClass}>Asunto</label>
              <input name="asunto" value={form.asunto} onChange={handleChange} className={inputClass} placeholder="Descripción del asunto" />
            </div>

            {/* Observación + Kilometraje */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Observación</label>
                <input name="observacion" value={form.observacion} onChange={handleChange} className={inputClass} placeholder="Observaciones adicionales" />
              </div>
              <div>
                <label className={labelClass}>Kilometraje</label>
                <input type="number" step="0.1" min="0" name="kilometraje" value={form.kilometraje} onChange={handleChange} className={inputClass} placeholder="0" />
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-800 flex justify-end gap-3 flex-none">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium border border-slate-600 transition-all duration-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-lg shadow-blue-900/20 transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
              {saving ? 'Guardando...' : 'Crear Caso'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default NuevoCasoModal;
